import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackHeroInteraction, trackCTAClick } from '../../lib/analytics';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import type { HeroSlide } from '../../types/firebase';

// Configuration i18n par cle de slide
const slideConfig: Record<string, {
  titleKey: string;
  subtitleKey: string;
  labelKey: string;
  benefitsPath: string;
  primaryCTAKey: string;
  secondaryCTAKey: string;
  primaryHref: string;
  secondaryHref: string;
}> = {
  mechanical: {
    titleKey: 'home.hero.mechanical.title',
    subtitleKey: 'home.hero.mechanical.subtitle',
    labelKey: 'home.hero.mechanical.label',
    benefitsPath: 'mechanical',
    primaryCTAKey: 'common.cta.requestQuote',
    secondaryCTAKey: 'common.cta.viewServices',
    primaryHref: '#contact',
    secondaryHref: '#services',
  },
  tires: {
    titleKey: 'home.hero.tires.title',
    subtitleKey: 'home.hero.tires.subtitle',
    labelKey: 'home.hero.tires.label',
    benefitsPath: 'tires',
    primaryCTAKey: 'common.cta.requestQuote',
    secondaryCTAKey: 'common.cta.viewServices',
    primaryHref: '#contact',
    secondaryHref: '#services',
  },
  custom: {
    titleKey: 'home.hero.custom.title',
    subtitleKey: 'home.hero.custom.subtitle',
    labelKey: 'home.hero.custom.label',
    benefitsPath: 'custom',
    primaryCTAKey: 'common.cta.briefProject',
    secondaryCTAKey: 'common.cta.viewProjects',
    primaryHref: '#custom-request',
    secondaryHref: '#gallery',
  },
};

const HeroCarousel: React.FC = () => {
  const { t, translations } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [rotationInterval] = useState(10000); // 10 seconds - un peu plus rapide

  const { data: heroSlides, loading, error } = useFirestoreCollection<HeroSlide>({
    collection: 'hero-slides',
    where: [['published', '==', true]],
    orderBy: ['order', 'asc'],
  });

  const slides = useMemo(() => {
    if (!heroSlides || heroSlides.length === 0) {
      // Fallback : slides avec placeholder gris (pas de crash)
      return Object.entries(slideConfig).map(([key, config]) => ({
        id: key,
        type: key,
        image: '', // placeholder gris via CSS
        title: t(config.titleKey),
        subtitle: t(config.subtitleKey),
        benefits: translations.home?.hero?.[config.benefitsPath as keyof typeof translations.home.hero]?.benefits || [],
        primaryCTA: t(config.primaryCTAKey),
        secondaryCTA: t(config.secondaryCTAKey),
        primaryHref: config.primaryHref,
        secondaryHref: config.secondaryHref,
      }));
    }

    return heroSlides.map((slide) => {
      const config = slideConfig[slide.key];
      if (!config) return null;
      return {
        id: slide.key,
        type: slide.key,
        image: slide.imageUrl,
        title: t(config.titleKey),
        subtitle: t(config.subtitleKey),
        benefits: translations.home?.hero?.[config.benefitsPath as keyof typeof translations.home.hero]?.benefits || [],
        primaryCTA: t(config.primaryCTAKey),
        secondaryCTA: t(config.secondaryCTAKey),
        primaryHref: config.primaryHref,
        secondaryHref: config.secondaryHref,
      };
    }).filter(Boolean) as Array<{
      id: string;
      type: string;
      image: string;
      title: string;
      subtitle: string;
      benefits: string[];
      primaryCTA: string;
      secondaryCTA: string;
      primaryHref: string;
      secondaryHref: string;
    }>;
  }, [heroSlides, t, translations]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    trackHeroInteraction(currentSlide, slides[currentSlide]?.type || 'unknown', 'next_slide');
  }, [slides.length]);

  const previousSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    trackHeroInteraction(currentSlide, slides[currentSlide]?.type || 'unknown', 'previous_slide');
  }, [slides.length]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(nextSlide, rotationInterval);
    return () => clearInterval(interval);
  }, [isPlaying, nextSlide, rotationInterval]);

  const handleMouseEnter = () => setIsPlaying(false);
  const handleMouseLeave = () => setIsPlaying(true);

  return (
    <section 
      id="home" 
      className="relative h-screen overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Background Image with Parallax Effect */}
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 20, ease: "linear" }}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: slides[currentSlide].image
                ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${slides[currentSlide].image})`
                : undefined,
              backgroundColor: slides[currentSlide].image ? undefined : '#374151',
            }}
          />

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />

          {/* Content */}
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-3xl">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="mb-6"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="w-12 h-12 rounded-full bg-gradient-to-r from-red-600 to-red-700 flex items-center justify-center"
                    >
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-red-600 rounded-full" />
                      </div>
                    </motion.div>
                    <span className="text-red-400 font-medium uppercase tracking-wider text-sm">
                      {slideConfig[slides[currentSlide].type]
                        ? t(slideConfig[slides[currentSlide].type].labelKey)
                        : ''
                      }
                    </span>
                  </div>

                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6"
                  >
                    {slides[currentSlide].title}
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.8 }}
                    className="text-lg sm:text-xl md:text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed"
                  >
                    {slides[currentSlide].subtitle}
                  </motion.p>

                  {/* Benefits */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.8 }}
                    className="mb-8"
                  >
                    <ul className="space-y-3">
                      {slides[currentSlide].benefits.map((benefit, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.1 + index * 0.1, duration: 0.6 }}
                          className="flex items-center space-x-3 text-gray-200"
                        >
                          <div className="w-2 h-2 bg-red-600 rounded-full flex-shrink-0" />
                          <span className="text-sm sm:text-base md:text-lg">{benefit}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>

                  {/* CTAs */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.8 }}
                    className="flex flex-col sm:flex-row gap-4"
                  >
                    <motion.a
                      href={slides[currentSlide].primaryHref}
                      onClick={() => trackCTAClick(slides[currentSlide].primaryCTA, 'hero_carousel', slides[currentSlide].primaryHref)}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
                    >
                      <span>{slides[currentSlide].primaryCTA}</span>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="ml-2"
                      >
                        →
                      </motion.div>
                    </motion.a>

                    <motion.a
                      href={slides[currentSlide].secondaryHref}
                      onClick={() => trackCTAClick(slides[currentSlide].secondaryCTA, 'hero_carousel', slides[currentSlide].secondaryHref)}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-200 text-sm sm:text-base"
                    >
                      {slides[currentSlide].secondaryCTA}
                    </motion.a>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Pills */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex items-center space-x-4 bg-black/30 backdrop-blur-sm rounded-full px-6 py-3">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => setCurrentSlide(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              aria-label={"Slide " + (index + 1)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-red-600 scale-125'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute inset-y-0 left-4 flex items-center z-20">
        <motion.button
          onClick={previousSlide}
          whileHover={{ scale: 1.1, x: -2 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Slide precedent"
          className="p-3 bg-black/30 backdrop-blur-sm text-white rounded-full hover:bg-black/50 transition-all duration-200"
        >
          <ChevronLeft className="w-6 h-6" />
        </motion.button>
      </div>

      <div className="absolute inset-y-0 right-4 flex items-center z-20">
        <motion.button
          onClick={nextSlide}
          whileHover={{ scale: 1.1, x: 2 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Slide suivant"
          className="p-3 bg-black/30 backdrop-blur-sm text-white rounded-full hover:bg-black/50 transition-all duration-200"
        >
          <ChevronRight className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Play/Pause Button */}
      <div className="absolute top-1/2 right-8 transform -translate-y-1/2 z-20">
        <motion.button
          onClick={() => setIsPlaying(!isPlaying)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          aria-label={isPlaying ? 'Pause' : 'Lecture'}
          className="p-3 bg-black/30 backdrop-blur-sm text-white rounded-full hover:bg-black/50 transition-all duration-200"
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </motion.button>
      </div>
    </section>
  );
};

export default HeroCarousel;